import { useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';
import Typography from 'src/common/components/Typography';
import TripCartDateInput from 'src/trip-cart-deprecated/components/TripCartDateInput';
import TripCartPanelContent from 'src/trip-cart-deprecated/components/panel/TripCartPanelContent';
import TripCartCaptainAndMultiday from 'src/trip-cart-deprecated/components/TripCartCaptainAndMultiDay';
import TripCartMessage from 'src/trip-cart-deprecated/components/TripCartMessage';
import TripCartGuests from 'src/trip-cart-deprecated/components/TripCartGuests';
import TripCartBookingButtons from 'src/trip-cart-deprecated/components/TripCartBookingButtons';
import TripCartDepartureTime from 'src/trip-cart-deprecated/components/TripCartDepartureTime';
import { useFormState } from 'react-final-form';
import { TripCartFormState } from 'src/listing/types';
import { FORM_ERROR } from 'final-form';
import TripCartContentFooter from 'src/trip-cart-deprecated/components/TripCartContentFooter';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import { BOOKING_MOTIVATOR_STATES } from 'src/common/constants';
import BookingMotivator from 'src/common/components/BookingMotivator';
import { useTripCartContext } from 'src/trip-cart-deprecated/hooks/useTripCartContext';
import { getDaysUntilInstabook } from 'src/common/utils/dateHelpers';
import { useTripCartOptionsContext } from 'src/trip-cart-deprecated/hooks/useTripCartOptionsContext';
import styles from './TripCartDrawerRoot.module.scss';

const MAX_REFUND = 100; // percent

const TripCartDrawerRoot: FC = () => {
  const [t] = useTranslation('listing-page');
  const [tCommon] = useTranslation('common');
  const { listing, instabookDates } = useTripCartContext();
  const { getSelectedMotivators } = useBookingMotivatorsContext();
  const { blockedTimes } = useTripCartOptionsContext();
  const {
    values: {
      formSelectedDates,
    },
    dirtySinceLastSubmit,
  } = useFormState<TripCartFormState>();
  const { submitErrors } = useFormState<TripCartFormState>();
  const [start, end] = formSelectedDates;

  const motivators = useMemo(() => (
    getSelectedMotivators([start, end])
  ), [end, getSelectedMotivators, start]);

  const daysUntilInstabook = getDaysUntilInstabook(formSelectedDates, instabookDates);

  const showMotivators = useMemo(
    () => motivators.some((motivator) => motivator !== BOOKING_MOTIVATOR_STATES.LOOKS_GOOD),
    [motivators],
  );

  const hasFreeCancellation = useMemo(() => {
    const {
      cancellation_refund: refund,
      cancellation_anticipation: anticipatedDays,
    } = listing;

    return !!anticipatedDays && refund >= MAX_REFUND;
  }, [listing]);

  return (
    <TripCartPanelContent
      title={t('trip-cart--drawer-title')}
      subtitle={hasFreeCancellation && (
        <div className={styles.freeCancellation}>
          <Typography type="span" size="body-s" weight="semibold">
            {tCommon('booking-motivator--free-cancellation--title')}
          </Typography>
          {' '}
          <Typography type="span" size="body-s">
            {tCommon('booking-motivator--free-cancellation--description', {
              count: listing.cancellation_anticipation,
              days: listing.cancellation_anticipation,
            })}
          </Typography>
        </div>
      )}
    >
      <div className={styles.root}>
        {showMotivators && (
          <div className={styles.bookingMotivators}>
            <BookingMotivator
              motivators={motivators}
              daysUntilInstabook={daysUntilInstabook}
              anticipatedDays={listing.cancellation_anticipation}
              blockedTimes={blockedTimes}
              size="xl"
            />
          </div>
        )}
        <TripCartCaptainAndMultiday />
        <TripCartDateInput />
        <div className={styles.departureTimeAndGuests}>
          <TripCartDepartureTime />
          <TripCartGuests />
        </div>
        <TripCartMessage />
        {!dirtySinceLastSubmit
          && submitErrors?.[FORM_ERROR] && (
            <Typography type="p" size="body-m" className={styles.formError}>
              {t(submitErrors?.[FORM_ERROR])}
            </Typography>
        )}
      </div>
      <TripCartContentFooter>
        <TripCartBookingButtons position="trip-cart-bottom-sheet--open" />
      </TripCartContentFooter>
    </TripCartPanelContent>
  );
};

export default TripCartDrawerRoot;
